<div class="focal-area-editor">
	<!-- Left Sidebar -->
	<div class="sidebar">
		<app-ai-suggestions
			[imageUrl]="imageUrl"
			(addSuggestion)="onAddSuggestion($event)"
			(previewSuggestion)="onPreviewSuggestion($event)">
		</app-ai-suggestions>

		<app-focal-area-sidebar
			[focalAreas]="visibleFocalAreas"
			[selectedIndex]="displayedSelectedIndex"
			[isSaving]="isSaving"
			[isLoadingImage]="isLoadingImage"
			[loadImageError]="loadImageError"
			[isAspectRatioDirty]="isAspectRatioDirty.bind(this)"
			[editingNameIndex]="displayedEditingIndex"
			[selectMode]="selectMode"
			[canSave]="hasUnsavedChanges"
			[isDirtyFocalArea]="isFocalAreaDirty.bind(this)"
			[tagFilterSelection]="selectedTagFilters"
			[shouldShowAutoGenerateButton]="shouldShowAutoGenerateButton"
			(addFocalArea)="onAddFocalArea()"
			(selectFocalArea)="onSelectFocalArea($event)"
			(updateFocalArea)="onUpdateFocalArea($event)"
			(deleteFocalArea)="onDeleteFocalArea($event)"
			(resetAspectRatio)="onResetAspectRatio($event)"
			(startEditingName)="onStartEditingName($event)"
			(finishEditingName)="onFinishEditingName()"
			(save)="onSave()"
			(copyCropData)="onCopyCropData()"
			(pasteCropData)="onPasteCropData()"
			(replaceImage)="onReplaceImage()"
			(loadImageFromUrl)="onLoadImageFromUrl($event)"
			(selectFocalAreaForTarget)="onSelectFocalAreaForTarget($event)"
			(tagFilterSelectionChange)="onTagFilterSelectionChange($event)"
			(copyFocalAreas)="copyFocalAreas()"
			(pasteFocalAreas)="pasteFocalAreas()"
			(autoGenerateFocalAreas)="autoGenerateFocalAreas()">
		</app-focal-area-sidebar>
	</div>

	<!-- Main Canvas -->
	<div class="canvas-container">
		<div class="canvas-area">
			<!-- Show preview when a preview mode is active (not 'original') -->
			<ng-container *ngIf="previewMode !== 'original' && selectedFocalArea; else editMode">
				<app-focal-area-preview
					[imageUrl]="getOptimizedImageUrl(imageUrl)"
					[focalArea]="selectedFocalArea"
					[previewMode]="previewMode"
					[customAspectRatio]="customAspectRatio"
					[imageNaturalWidth]="imageNaturalWidth"
					[imageNaturalHeight]="imageNaturalHeight"
					[autoDetectMode]="autoDetectMode"
					[filterTags]="selectedTagFilters"
					(detectionResult)="onDetectionResult($event)">
				</app-focal-area-preview>
			</ng-container>

			<!-- Show cropper in edit mode (original or no preview mode) -->
			<ng-template #editMode>
				<ng-container *ngIf="imageUrl && selectedFocalArea; else noSelection">
					<image-cropper
						[imageURL]="getOptimizedImageUrl(imageUrl)"
						[backgroundColor]="'#f5f5f5'"
						[maintainAspectRatio]="false"
						[imagePosition]="currentCropperPosition"
						(imageCropped)="onImageCropped($event)"
						(cropperReady)="onCropperReady()"
						(imageLoaded)="onImageLoaded($event)"
					></image-cropper>
				</ng-container>

				<ng-template #noSelection>
					<img *ngIf="imageUrl" [src]="getOptimizedImageUrl(imageUrl)" alt="Editing image" class="canvas-image">
					<div *ngIf="!imageUrl" class="no-image-placeholder">
						<mat-icon>image</mat-icon>
						<p>No image loaded</p>
					</div>
				</ng-template>
			</ng-template>

			<!-- Hidden image to capture dimensions when cropper is not visible -->
			<img *ngIf="imageUrl && !imageNaturalWidth"
				 [src]="getOptimizedImageUrl(imageUrl)"
				 (load)="onHiddenImageLoad($event)"
				 style="position: absolute; visibility: hidden; width: 0; height: 0;">
		</div>

		<!-- Detection Info Bar (shown when auto-detect is enabled) -->
		<div *ngIf="autoDetectMode" class="detection-info-bar">
			<div class="detection-result">
				<ng-container *ngIf="isLoadingDetection">
					<mat-icon class="loading-icon">sync</mat-icon>
					<span class="loading-text">Detecting focal areas...</span>
				</ng-container>

				<ng-container *ngIf="!isLoadingDetection && detectionResult">
					<!-- Success: Focal area was selected -->
					<ng-container *ngIf="detectionResult.selectedFocalArea && !detectionResult.fallbackReason">
						<mat-icon class="success-icon">check_circle</mat-icon>
						<span class="success-text">{{ detectionResult.selectedFocalArea }}</span>
					</ng-container>

					<!-- Fallback: No match found -->
					<ng-container *ngIf="detectionResult.fallbackReason">
						<mat-icon class="fallback-icon">info</mat-icon>
						<span class="fallback-text">Fallback: {{ detectionResult.fallbackReason }}</span>
					</ng-container>
				</ng-container>
			</div>
		</div>

		<!-- Preview Mode Bar -->
		<div class="preview-bar">
			<button class="preview-btn icon-btn"
				[class.active]="autoDetectMode"
				(click)="toggleAutoDetect()"
				[attr.title]="'Auto Detect'">
				<mat-icon>auto_awesome</mat-icon>
			</button>

			<button class="preview-btn"
				[class.active]="previewMode === 'original'"
				[disabled]="!selectedFocalArea"
				(click)="setPreviewMode('original')">
				Edit Mode
			</button>
			<button class="preview-btn"
				[class.active]="previewMode === '1:1'"
				[disabled]="!selectedFocalArea"
				(click)="setPreviewMode('1:1')">
				Square
			</button>
			<button class="preview-btn"
				[class.active]="previewMode === '16:9'"
				[disabled]="!selectedFocalArea"
				(click)="setPreviewMode('16:9')">
				16:9
			</button>
			<button class="preview-btn"
				[class.active]="previewMode === '9:16'"
				[disabled]="!selectedFocalArea"
				(click)="setPreviewMode('9:16')">
				9:16
			</button>
			<button class="preview-btn"
				[class.active]="previewMode === '4:3'"
				[disabled]="!selectedFocalArea"
				(click)="setPreviewMode('4:3')">
				4:3
			</button>
			<button class="preview-btn"
				[class.active]="previewMode === '3:4'"
				[disabled]="!selectedFocalArea"
				(click)="setPreviewMode('3:4')">
				3:4
			</button>
			<button class="preview-btn"
				[class.active]="previewMode === 'custom'"
				[disabled]="!selectedFocalArea"
				(click)="setPreviewMode('custom')">
				Custom
			</button>
			<input *ngIf="previewMode === 'custom'"
				type="text"
				class="custom-aspect-input"
				[disabled]="!selectedFocalArea"
				[(ngModel)]="customAspectRatio"
				placeholder="16:9"
				(blur)="onCustomAspectRatioChange()">
		</div>
	</div>
</div>
